import produce from 'immer';
import { message } from 'antd';
import {
  mutateDeleteSection,
  mutateHandleLinkedSectionRecursive,
  mutateInjectSection,
} from '../utils/sections';
import { mutateRemoveContainer } from '../utils/containers';
import {
  applyTableConfigPresentation,
  fillEmptyAdminTablesBoxes,
  fetchTableData,
  getTableColumnsData,
} from '../utils/table';
import { rowKindsOf } from '../shared/tables/tableColumns';
import { isATableBox, isAImgBox } from '../utils/boxes';
import { computeColumnWidth, containerColumnGap, getContainerColumnIds } from '../utils/columns';
import { buildUniqueContentData } from '../utils/dataSync';
import { mutateInjectVariablesInBox } from '../utils/variables';
import { getTable } from '../requests/documentDatas';
import {
  ItemTypes,
  MASKS_TYPE,
  RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS,
  tableConfigForSplitScope,
} from '../constants/constants';
import { gridUnit } from '../constants/gridConfig';
import { isFixedImageFit } from '../shared/boxes/imageFit';
import {
  substituteRecursiveVariables,
  getGroupedRowsFromTableResponse,
  getRowsFromTableResponse,
  getSectionRecursiveMeta,
  hasUnresolvedRecursiveVariables,
  shouldSkipRecursiveReloadExpansion,
  getBakedRecursiveInstances,
  collectNoRepeatExclusions,
  filterSectionContentNoRepeat,
  excludeFromContent,
} from '../utils/recursiveSections';
import { getRecursiveSectionData } from '../requests/recursiveSections';

// Filet de sécurité si une identité de cycle passait entre les mailles.
const RECURSIVE_SECTION_MAX_DEPTH = 20;

// Clé du pré-fetch récursif : nom de table + axes donnée. Le nom est normalisé en
// majuscules (les box mélangent les deux casses).
const splitTableCacheKey = (tableName, tableConfig) =>
  `${String(tableName).toUpperCase()}|${JSON.stringify(tableConfig ?? null)}`;

// Chrono par opportunité d'une offre, mémoïsé : une section imbriquée ne reçoit
// que l'id de son opportunité, et chaque niveau d'imbrication rappellerait
// sinon l'endpoint pour la même table. Clé = id de l'offre.
const _offerOpportunityChronos = new Map();

const getOpportunityChronoMap = (offerId) => {
  if (!_offerOpportunityChronos.has(offerId)) {
    _offerOpportunityChronos.set(
      offerId,
      getRecursiveSectionData(offerId, 'opportunity', { documentType: 'offer' })
        .then((rows) => new Map((rows ?? []).map((r) => [String(r._id), r.chrono])))
        // Un échec réseau ne doit pas se figer dans le cache : sans cet oubli,
        // toutes les injections suivantes de la session resteraient sans chrono.
        .catch(() => {
          _offerOpportunityChronos.delete(offerId);
          return new Map();
        })
    );
  }
  return _offerOpportunityChronos.get(offerId);
};

/**
 * Rattache les variables document d'un gabarit à une opportunité précise :
 * `opportunity.NAME` -> `<CHRONO>.opportunity.NAME`, la forme à 3 segments que
 * l'offre sait déjà résoudre (cf. formatVariables, utils/dataSync.js). Rend
 * null pour toute clé sans équivalent préfixé — custom.*, offer.* — qui reste
 * alors intacte.
 */
const buildVariableRemap = (chrono, variables) => {
  if (!chrono) return null;
  return (varKey) => {
    const scopedKey = `${chrono}.${varKey}`;
    return variables[scopedKey] ? scopedKey : null;
  };
};

// Lignes du modèle d'une section récursive, avec les mêmes paramètres que ceux
// qu'utilise injectRecursiveSection : une réinitialisation les récupère AVANT de
// supprimer le contenu en place, puis les lui passe.
// parentModel/parentId scope the root fetch when this recursive section is itself nested
// under another recursive instance (depth >= 3, e.g. work_position -> tenue -> clothing).
// Without them the grandchild would fetch ALL rows of its model (offer endpoint, unscoped).
// force:true à l'insertion => relance la regen des screenshots planning/SP
// (l'image revient vide -> loader, puis swappée via le broadcast WS). Sans
// effet sur les modèles sans image (le resolver ne regénère que work_position).
const fetchRecursiveSectionRows = (
  currentDocument,
  { section, opportunityIdOverride = null, documentTypeOverride = null, parentModel = null, parentId = null }
) =>
  getRecursiveSectionData(
    opportunityIdOverride ?? currentDocument.id,
    section.recursiveModelName,
    {
      parentModel,
      parentId,
      documentType: documentTypeOverride ?? currentDocument.type,
      agreementType: section.recursiveAgreementType ?? section.content?.recursiveAgreementType,
      sectionWork: section.work ?? section.content?.work,
      force: true,
    }
  );

// Le contenu injecté arrive avec le libellé des variables, pas leur valeur :
// mutateInjectSection ne les résout pas. Une passe sur les seules box de cette
// injection les résout toutes d'un coup (les clés inconnues sont ignorées, cf.
// replaceVariableText).
// Une box sans variable du modèle traverse substituteRecursiveVariables telle
// quelle : son `content` est encore celui du modèle en cache, que immer a gelé
// au premier commit. On résout donc sur une copie, sans quoi la passe lève sur
// la 2e injection et emporte tout le segment.
export const mutateResolveInjectedVariables = (draft, dateTimestamp) => {
  draft.boxes.forEach((b, index) => {
    if (b.dateTimestamp !== dateTimestamp) return;
    const box = { ...b, content: b.content && { ...b.content } };
    mutateInjectVariablesInBox({ variables: draft.variables, box });
    draft.boxes[index] = box;
  });
};

// Retire le container de l'ancre d'une section récursive une fois consommée.
// Exception : une ancre SECTION_VARIABLE est une question posée à
// l'utilisateur, pas un emplacement à consommer — la retirer emportait le
// widget (et tout ce que son container hébergeait), sans moyen de repasser à
// « Non ». Même garde que mutateInjectSection sur le chemin non récursif.
const mutateRemoveRecursivePlaceholder = (draft, boxId) => {
  const placeholderBox = draft.boxes.find((b) => b.id === boxId);
  if (!placeholderBox) return;
  if (placeholderBox.type === ItemTypes.SECTION_VARIABLE) return;
  const containerIdx = draft.containers.findIndex((c) =>
    c.columnsIds.includes(placeholderBox.columnId)
  );
  if (containerIdx === -1) return;
  mutateRemoveContainer(draft, draft.containers[containerIdx].id);
};

export const sectionsActions = (set, get) => ({
  injectSection: async ({ id, boxId, linked_section = false, split = false }) => {
    const section = await get().getTemplate(id);
    if (!section) return;
    const currentDocument = get().currentDocument;

    const sectionIsRecursive = section.isRecursive ?? section.content?.isRecursive;
    const sectionRecursiveModelName = section.recursiveModelName ?? section.content?.recursiveModelName;
    if (
      sectionIsRecursive &&
      sectionRecursiveModelName &&
      currentDocument.type !== 'template'
    ) {
      await get().injectRecursiveSection({
        section: { ...section, isRecursive: sectionIsRecursive, recursiveModelName: sectionRecursiveModelName },
        boxId,
        split,
      });
      return;
    }

    // In template mode, if this recursive section was already injected at this anchor
    // (widget stays visible because from_linked_section=true prevents container removal),
    // clear the baked content first to avoid stacking on top of existing content.
    if (currentDocument.type === 'template' && sectionIsRecursive && sectionRecursiveModelName) {
      const isAlreadyExpanded = get().boxes.some(
        (b) => b.fromBoxId === boxId && String(b.fromSectionId) === String(section.id)
      );
      if (isAlreadyExpanded) {
        set(
          produce((draft) => {
            mutateDeleteSection({ draft, boxId });
          }),
          false,
          `Clear recursive section before re-inject in template ${boxId}`
        );
      }
    }

    const sectionContent =
      currentDocument.type === 'template'
        ? section.content
        : {
            ...section.content,
            ...(await fillEmptyAdminTablesBoxes({
              boxes: section.content.boxes,
              containers: section.content.containers,
              columns: section.content.columns,
              proposable_id: currentDocument.id,
              type: currentDocument.type,
              landscape: get().landscape,
              marginRight:
                section.content.configuration.margins.right * gridUnit,
              marginLeft: section.content.configuration.margins.left * gridUnit,
              header: section.content.masks.find(
                ({ id }) => id === MASKS_TYPE.HEADER.id
              ),
              footer: section.content.masks.find(
                ({ id }) => id === MASKS_TYPE.FOOTER.id
              ),
              gridConfiguration: section.content.gridConfiguration,
              configuration: section.content.configuration,
              triggers: section.content.triggers,
              variableOrderArray: section.content.variableOrderArray,
            })),
          };
    set(
      produce((draft) => {
        mutateInjectSection({
          draft,
          sectionContent,
          boxId,
          sectionId: section.id,
          linked_section,
        });
      }),
      false,
      `Inject section ${id}`
    );
    if (sectionIsRecursive && sectionRecursiveModelName && currentDocument.type === 'template') {
      const sectionAgreementType =
        section.recursiveAgreementType ?? section.content?.recursiveAgreementType;
      await get().fetchRecursiveSectionFields(sectionRecursiveModelName, sectionAgreementType);
      // Derive which recursive fields are actually referenced in the section content so
      // the form shows them (notUsed:false). Mirrors the opportunity path in
      // injectRecursiveSection; without it every field stays notUsed and the row is hidden.
      const usedFieldKeys = new Set();
      const recursivePrefix = `${sectionRecursiveModelName}.`;
      for (const box of section.content.boxes) {
        const raw = box.content?.raw;
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          Object.values(parsed.entityMap || {}).forEach((entity) => {
            if (entity.type !== 'VARIABLE') return;
            const k = entity.data?.key || '';
            if (k.startsWith(recursivePrefix)) {
              const rest = k.slice(recursivePrefix.length);
              if (!rest.includes('.')) usedFieldKeys.add(rest);
            }
          });
        } catch {}
      }
      get().syncRecursiveVariablesToStore(sectionRecursiveModelName, usedFieldKeys, sectionAgreementType);
    }
  },

  injectRecursiveSection: async ({ section, boxId, split = false, opportunityIdOverride = null, documentTypeOverride = null, parentModel = null, parentId = null, visitedPath = new Set(), prefetchedRows }) => {
    const currentDocument = get().currentDocument;
    const opportunityId = opportunityIdOverride ?? currentDocument.id;
    const documentType = documentTypeOverride ?? currentDocument.type;
    const modelName = section.recursiveModelName;
    const agreementType = section.recursiveAgreementType ?? section.content?.recursiveAgreementType;
    const sectionWork = section.work ?? section.content?.work;

    // Base de chemin pour l'arbre des sections (cf. utils/sectionTree.js) : chaîne
    // d'ancêtres de l'ancre récursive. Le contenu baké hérite [...basePath, section.id]
    // (segment parent) ou [...basePath, section.id, childTemplate.id] (lignes enfant),
    // ce qui compose correctement pour les imbrications profondes.
    const anchorBox = get().boxes.find((b) => b.id === boxId);
    const basePath = Array.isArray(anchorBox?.sectionPath) ? anchorBox.sectionPath : [];

    // Cycle guard: le graphe de nesting backend (MASTER_RELATIONSHIPS) autorise des
    // aller-retours légitimes (ex. materiel <-> prestation, materiel <-> materiel).
    // Si la même ligne réapparaît dans sa propre descendance (accessoires liés en boucle
    // côté data), deux appels mutuellement récursifs tournent à l'infini. Identité =
    // modèle + ligne concrète (pas le sectionId du template) pour ne pas bloquer les
    // imbrications non-cycliques légitimes du même modèle.
    const identity = `${modelName}:${parentId ?? opportunityId}`;
    if (visitedPath.has(identity) || visitedPath.size >= RECURSIVE_SECTION_MAX_DEPTH) {
      console.warn(`[injectRecursiveSection] cycle or max depth reached, skipping: ${identity}`);
      return;
    }
    const nextVisitedPath = new Set(visitedPath).add(identity);

    // Section imbriquée sous une copie de modèle récursif : elle décrit UNE
    // opportunité, comme le gabarit qui la porte, et ses variables document
    // doivent lire les données de cette opportunité-là. Seul l'id descend le
    // long de la chaîne d'injection ; le chrono, lui, est le seul identifiant
    // présent des deux côtés (ligne et variables du document), d'où le détour
    // par la table des opportunités de l'offre. Résolu après la garde de cycle,
    // pour ne pas lancer la requête sur un appel qui s'arrête là.
    let remapVariableKey = null;
    if (opportunityIdOverride && currentDocument.type === 'offer' && currentDocument.id) {
      const chronos = await getOpportunityChronoMap(currentDocument.id);
      remapVariableKey = buildVariableRemap(
        chronos.get(String(opportunityIdOverride)),
        get().variables
      );
    }

    const rows =
      prefetchedRows !== undefined
        ? prefetchedRows
        : await fetchRecursiveSectionRows(currentDocument, {
            section,
            opportunityIdOverride,
            documentTypeOverride,
            parentModel,
            parentId,
          });
    if (!rows) return;
    if (rows.length === 0) {
      // Ancre imbriquée (sous une ligne parente, ou sous une opportunité d'une
      // copie de modèle récursif) sans ligne : laissée brute, elle était dépliée
      // au chargement suivant SANS cette portée — toutes les lignes du modèle,
      // celles des autres parents comprises. Une ancre de premier niveau garde
      // son emplacement : la déplier à nouveau donne la même portée.
      if (parentModel != null || opportunityIdOverride != null) {
        set(
          produce((draft) => mutateRemoveRecursivePlaceholder(draft, boxId)),
          false,
          `Remove empty nested recursive placeholder ${boxId}`
        );
      }
      return;
    }

    // Resolve valid recursive child configs from the parent template content.
    // Two sources:
    //   1. Explicit RECURSIVE_SECTION placeholder boxes (manual approach)
    //   2. Boxes tagged with a foreign sectionId from an embedded recursive child section
    const resolveChildConfigs = async () => {
      const relationships = get().recursiveSectionRelationships;

      const isValidChild = (childModelName) => {
        if (!childModelName) return false;
        // opportunity parent allows any non-opportunity child (arbitrary nested sections)
        if (modelName === 'opportunity') return childModelName !== 'opportunity';
        // If relationships are not loaded yet, don't block explicit embedded child detection.
        if (!relationships || Object.keys(relationships).length === 0) return true;
        // If agreementType set, use scoped relationships; otherwise check all agreements
        if (agreementType) {
          return relationships?.[agreementType]?.[modelName]?.includes(childModelName);
        }
        return Object.values(relationships || {}).some(
          (rels) => rels[modelName]?.includes(childModelName)
        );
      };

      const configs = [];

      // Source 1: explicit RECURSIVE_SECTION boxes
      const explicitBoxes = section.content.boxes.filter(
        (b) => b.type === ItemTypes.RECURSIVE_SECTION && b.content?.childTemplateId
      );
      for (const box of explicitBoxes) {
        const childTemplate = await get().getTemplate(box.content.childTemplateId);
        if (!childTemplate) continue;
        configs.push({ childTemplate, childModelName: box.content.childModelName, explicit: true });
      }

      // Source 2: detect embedded child sections via persisted section markers.
      // Depending on how content was saved, marker can be on:
      // - box.sectionId (linked section flow)
      // - box.fromSectionId (baked injected content)
      // - container.sectionId (baked injected containers)
      const foreignSectionIds = new Set();
      const pushForeignSectionId = (candidateId) => {
        if (candidateId == null) return;
        if (String(candidateId) === String(section.id)) return;
        foreignSectionIds.add(candidateId);
      };
      for (const box of section.content.boxes) {
        pushForeignSectionId(box.sectionId);
        pushForeignSectionId(box.fromSectionId);
      }
      for (const container of section.content.containers || []) {
        pushForeignSectionId(container.sectionId);
      }

      for (const childSectionId of foreignSectionIds) {
        const childTemplate = await get().getTemplate(childSectionId);
        if (!childTemplate) continue;
        const childIsRecursive = childTemplate.isRecursive ?? childTemplate.content?.isRecursive;
        const childModelName = childTemplate.recursiveModelName ?? childTemplate.content?.recursiveModelName;
        if (!childIsRecursive || !childModelName) continue;
        if (!isValidChild(childModelName)) continue;
        configs.push({ childTemplate, childModelName, embeddedSectionId: childSectionId });
      }

      return configs;
    };

    const childConfigs = await resolveChildConfigs();

    // Build the set of boxes/containers/columns to exclude from parent content.
    // Exclude: explicit RECURSIVE_SECTION boxes + embedded child section items.
    const excludedBoxIds = new Set(
      section.content.boxes
        .filter((b) => b.type === ItemTypes.RECURSIVE_SECTION)
        .map((b) => b.id)
    );
    const excludedContainerIds = new Set();

    const embeddedSectionIds = new Set(
      childConfigs
        .filter((c) => c.embeddedSectionId != null)
        .map((c) => String(c.embeddedSectionId))
    );

    for (const box of section.content.boxes) {
      const boxSectionId = box.sectionId != null ? String(box.sectionId) : null;
      const boxFromSectionId =
        box.fromSectionId != null ? String(box.fromSectionId) : null;
      if (
        (boxSectionId && embeddedSectionIds.has(boxSectionId)) ||
        (boxFromSectionId && embeddedSectionIds.has(boxFromSectionId))
      ) {
        excludedBoxIds.add(box.id);
      }
    }
    for (const container of section.content.containers || []) {
      const containerSectionId =
        container.sectionId != null ? String(container.sectionId) : null;
      if (containerSectionId && embeddedSectionIds.has(containerSectionId)) {
        excludedContainerIds.add(container.id);
      }
    }

    // noRepeat : gardé une seule fois, sur la première ligne (rowIndex 0), à sa position
    // naturelle dans le template ; exclu de toutes les lignes suivantes. Volontairement PAS
    // fusionné dans excludedContainerIds/etc ci-dessus (celles-ci excluent les enfants sur
    // TOUTES les lignes, sans exception) — cf. rowExcluded*Ids dans injectWithChildren.
    const noRepeatExclusions = collectNoRepeatExclusions(section.content);

    // General table fill: every TABLE_VARIABLE gets its normal (non-split) data once,
    // shared across all injected rows. Handles non-zone recursive models and any
    // table without a zone-split variant.
    const filledContentBase = documentType !== 'template'
      ? {
          ...section.content,
          ...(await fillEmptyAdminTablesBoxes({
            boxes: section.content.boxes,
            containers: section.content.containers,
            columns: section.content.columns,
            proposable_id: opportunityId,
            type: documentType,
            landscape: get().landscape,
            marginRight: (section.content.configuration?.margins?.right ?? 0) * gridUnit,
            marginLeft: (section.content.configuration?.margins?.left ?? 0) * gridUnit,
            header: section.content.masks?.find(({ id }) => id === MASKS_TYPE.HEADER.id),
            footer: section.content.masks?.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
            gridConfiguration: section.content.gridConfiguration ?? get().gridConfiguration,
            configuration: section.content.configuration ?? get().configuration,
            triggers: section.content.triggers ?? {},
            variableOrderArray: section.content.variableOrderArray ?? [],
          })),
        }
      : section.content;

    // Image planning récursive : forcer pleine largeur de colonne (comme un tableau
    // variable plein cadre) au lieu de la taille placeholder du gabarit. height 'auto'
    // => ratio conservé, pas de distorsion. Appliqué une fois sur filledContent : tous
    // les chemins d'injection (segments enfants + fallback) en dérivent.
    const PLANNING_IMG_FIELDS = RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS;
    const widenPlanningImageBox = (box) => {
      // Contenir / remplir : la box garde la taille choisie.
      if (!isAImgBox(box.type) || isFixedImageFit(box)) return box;
      const parts = (box.content?.adminValue || '').split('.');
      if (parts.length !== 2 || parts[0] !== modelName || !PLANNING_IMG_FIELDS.includes(parts[1])) return box;
      const column = (filledContentBase.columns || []).find((c) => c.id === box.columnId);
      const container = (filledContentBase.containers || []).find((c) => c.columnsIds?.includes(box.columnId));
      const colWidth = computeColumnWidth({
        landscape: get().landscape,
        marginRight: (section.content.configuration?.margins?.right ?? 0) * gridUnit,
        marginLeft: (section.content.configuration?.margins?.left ?? 0) * gridUnit,
        containerColumnsLength: container?.columnsIds?.length ?? 1,
        size: column?.size ?? 1,
        columnGap: containerColumnGap(container),
      });
      if (!colWidth || colWidth <= 0) return box;
      return { ...box, width: colWidth, height: 'auto', content: { ...box.content, ratio: null } };
    };
    const filledContent = {
      ...filledContentBase,
      boxes: (filledContentBase.boxes || []).map(widenPlanningImageBox),
    };

    // Row-splittable models: pre-fetch a per-row split of each table so every looped
    // instance renders only its own slice (rows_<row._id>) instead of the all-rows table.
    // - zone: the box uses "NAME_WORK"; we fetch its "NAME_ZONES_WORK" splittable variant.
    // - work_position / profil: the box already targets the splittable table directly, so we
    //   fetch the same name with split:true. Tables without split support return a flat array
    //   (no rows_ keys) and are skipped below. Keyed by the original box.tableName.
    const zoneSplitTableData = {};
    const SPLIT_MODELS = ['zone', 'work_position', 'profil', 'materiel', 'tenue', 'formation', 'prestation'];
    const resolveSplitTableName = (name) => {
      if (modelName === 'zone') {
        return /_ZONES_WORK$/.test(name) ? name : (/_WORK$/.test(name) ? name.replace(/_WORK$/, '_ZONES_WORK') : null);
      }
      // work_position / profil / materiel / tenue / formation / prestation: no name variant,
      // split happens on the same table (backend returns rows_<row._id>).
      return name;
    };
    if (documentType !== 'template' && SPLIT_MODELS.includes(modelName)) {
      const tableBoxes = (filledContent.boxes || []).filter(
        (b) => isATableBox(b.type) && b.tableName && resolveSplitTableName(b.tableName)
      );
      // Une entrée par couple (table, axes donnée) : deux box de la même table
      // réglées différemment dans le Drawer ne doivent pas partager une réponse.
      const uniqueFetches = new Map();
      tableBoxes.forEach((b) => {
        const tableConfig = tableConfigForSplitScope(b.tableName, b.tableConfig);
        uniqueFetches.set(splitTableCacheKey(b.tableName, tableConfig), {
          tableName: b.tableName,
          tableConfig,
        });
      });
      await Promise.all(
        [...uniqueFetches].map(async ([cacheKey, { tableName, tableConfig }]) => {
          const raw = await getTable(
            { id: opportunityId, type: documentType },
            resolveSplitTableName(tableName),
            { split: true, tableConfig }
          );
          // Only keep it if the backend actually split by row (has rows_<id> keys).
          if (raw && Object.keys(raw).some((k) => /^rows_/.test(k))) {
            zoneSplitTableData[cacheKey] = raw;
          }
        })
      );
    }

    // Inject all rows keeping placeholder alive, then remove it at end.
    // Order matters: mutateInjectSection prepends before the placeholder.
    // Strategy: scan template containers to build an ordered segment plan, then
    // inject in REVERSE order so each prepend lands in the correct final position.
    // Example: [title][profil_div][line1][formation_div][line2]
    // → Inject: line2 → formation_rows → line1 → profil_rows → title
    // → Result: [title][profil_rows][line1][formation_rows][line2] ✓
    const injectWithChildren = async (row, targetBoxId, timestamp, rowIndex) => {
      const allContainers = filledContent.containers || [];
      const allColumns = filledContent.columns || [];
      const allBoxes = filledContent.boxes;

      // noRepeat : la première ligne (rowIndex 0, la plus haute visuellement — cf. l'ordre
      // d'injection inversé plus haut) garde ce contenu ; les suivantes l'excluent comme les
      // enfants. getChildConfigForContainer continue d'utiliser les sets non-row-aware
      // (exclusion d'enfants, valable pour toutes les lignes) — seuls le scan de segments et
      // le fallback "pas d'enfant" ci-dessous utilisent les versions row-aware.
      const isFirstRow = rowIndex === 0;
      const rowExcludedContainerIds = isFirstRow
        ? excludedContainerIds
        : new Set([...excludedContainerIds, ...noRepeatExclusions.excludedContainerIds]);
      const rowExcludedBoxIds = isFirstRow
        ? excludedBoxIds
        : new Set([...excludedBoxIds, ...noRepeatExclusions.excludedBoxIds]);

      // Zone model: replace the (all-zones) table data with only the current zone's
      // slice (rows_<zone.id>). Box already has normal data from fillEmptyAdminTablesBoxes;
      // this overrides it. If the zone has no slice, keep the table with its headers
      // and no row: the section is injected as designed.
      const applyZoneTableData = (box) => {
        if (!isATableBox(box.type) || !box.tableName) return box;
        const tableData =
          zoneSplitTableData[
            splitTableCacheKey(
              box.tableName,
              tableConfigForSplitScope(box.tableName, box.tableConfig)
            )
          ];
        if (!tableData) return box;
        const zoneRows = tableData[`rows_${String(row._id)}`];
        if (!zoneRows?.length) {
          return {
            ...box,
            content: { ...box.content, data: [], shouldSplit: false, recursiveRowId: row._id },
          };
        }
        // Axes de PRÉSENTATION (colonnes masquées / renommées / réordonnées /
        // largeurs) : ils vivent dans tableConfig.columns et s'appliquent
        // entièrement côté client, comme dans computeTableBoxContents
        // (utils/table.js). Sans cet appel la table sortait brute de l'API et
        // une colonne masquée dans le Drawer réapparaissait ici.
        // Les axes DONNÉE, eux, sont partis au fetch de split ci-dessus, filtrés
        // par tableConfigForSplitScope (cf. le flag splitEntity).
        const tableContent = applyTableConfigPresentation(
          getTableColumnsData(
            {
              rows: zoneRows,
              columns_informations: tableData.columns_informations,
              kinds: rowKindsOf(tableData, `rows_${String(row._id)}`),
            },
            box.width
          ),
          box.tableConfig,
          box.width,
          box.tableName
        );
        const { columns, data, columnsDisposition } = buildUniqueContentData(tableContent);
        // recursiveRowId : même pattern que les images planning récursives — le
        // re-fetch WS (refreshPlanningScreenshotTables) ne doit recharger que
        // rows_<poste.id>, pas la table complète.
        return {
          ...box,
          content: {
            ...box.content,
            columns,
            data,
            columnsDisposition,
            mergedCells: [],
            shouldSplit: false,
            recursiveRowId: row._id,
          },
        };
      };

      // Toutes les colonnes d'un container, sous-colonnes de ses lignes
      // imbriquées comprises : ses columnsIds ne portent que le premier niveau.
      const containerColumnIds = (container) =>
        getContainerColumnIds({
          container,
          columns: allColumns,
          rowContainers: filledContent.rowContainers ?? [],
        });

      const buildContentFromContainers = (containers) => {
        const columnIds = new Set(containers.flatMap(containerColumnIds));
        const content = excludeFromContent(
          {
            ...section.content,
            containers,
            columns: allColumns.filter((col) => columnIds.has(col.id)),
            boxes: allBoxes.filter((b) => columnIds.has(b.columnId)),
          },
          { boxIds: rowExcludedBoxIds }
        );
        return { ...content, boxes: content.boxes.map(applyZoneTableData) };
      };

      // Return the childConfig for which this container is a divider, or null.
      // Uses String() on all id comparisons to handle int/string mismatches.
      const getChildConfigForContainer = (c) => {
        // Case 1: container itself is excluded (Source 2, container.sectionId set).
        if (excludedContainerIds.has(c.id)) {
          const sid = c.sectionId != null ? String(c.sectionId) : null;
          if (!sid) return null;
          return (
            childConfigs.find(
              (cfg) => cfg.embeddedSectionId != null && String(cfg.embeddedSectionId) === sid
            ) || null
          );
        }
        // Case 2: container holds an excluded box (any type — RS placeholder or baked box
        // with box.sectionId/fromSectionId set by a previous injection).
        const columnIds = containerColumnIds(c);
        const excBox = allBoxes.find(
          (b) =>
            columnIds.some((colId) => String(b.columnId) === String(colId)) &&
            excludedBoxIds.has(b.id)
        );
        if (!excBox) return null;
        // Explicit RS placeholder box.
        if (excBox.type === ItemTypes.RECURSIVE_SECTION) {
          return (
            childConfigs.find(
              (cfg) =>
                cfg.explicit &&
                String(cfg.childTemplate.id) === String(excBox.content?.childTemplateId)
            ) || null
          );
        }
        // Baked box with sectionId/fromSectionId pointing to an embedded child section.
        const sid =
          excBox.sectionId != null
            ? String(excBox.sectionId)
            : excBox.fromSectionId != null
              ? String(excBox.fromSectionId)
              : null;
        if (!sid) return null;
        return (
          childConfigs.find(
            (cfg) => cfg.embeddedSectionId != null && String(cfg.embeddedSectionId) === sid
          ) || null
        );
      };

      // Scan template containers once to build an ordered segment plan.
      const segments = [];
      let parentBatch = [];
      const seenChildIds = new Set();

      const flushParent = () => {
        if (parentBatch.length > 0) {
          segments.push({ type: 'parent', containers: [...parentBatch] });
          parentBatch = [];
        }
      };

      for (const c of allContainers) {
        const cfg = getChildConfigForContainer(c);
        if (cfg) {
          const childId = String(cfg.childTemplate.id);
          if (!seenChildIds.has(childId)) {
            flushParent();
            segments.push({ type: 'child', config: cfg });
            seenChildIds.add(childId);
          }
        } else if (!rowExcludedContainerIds.has(c.id)) {
          parentBatch.push(c);
        }
      }
      flushParent();

      const segmentedChildIds = new Set(
        segments.filter((s) => s.type === 'child').map((s) => String(s.config.childTemplate.id))
      );

      // Children from childConfigs not found in the template scan (unpositioned).
      // Appended at end of segments so they are always injected.
      const unpositionedConfigs = childConfigs.filter(
        (cfg) => !segmentedChildIds.has(String(cfg.childTemplate.id))
      );
      for (const cfg of unpositionedConfigs) {
        segments.push({ type: 'child', config: cfg });
      }

      const hasAnyChildren =
        childConfigs.length > 0 || segments.some((s) => s.type === 'child');

      // No children at all — inject full parent as a single block.
      if (!hasAnyChildren) {
        const keptContent = excludeFromContent(
          { ...section.content, boxes: allBoxes, containers: allContainers, columns: allColumns },
          { containerIds: rowExcludedContainerIds, boxIds: rowExcludedBoxIds }
        );
        const fallbackContent = {
          ...keptContent,
          boxes: keptContent.boxes.map(applyZoneTableData),
        };
        const substitutedFallback = substituteRecursiveVariables(fallbackContent, row, modelName, { remapVariableKey });
        if (!substitutedFallback.containers?.length) return;
        set(
          produce((draft) => {
            mutateInjectSection({
              draft,
              sectionContent: substitutedFallback,
              boxId: targetBoxId,
              sectionId: section.id,
              dateTimestamp: timestamp,
              skipContainerRemoval: true,
              sectionsIds: [...basePath, section.id],
              visitedPath: nextVisitedPath,
            });
            if (remapVariableKey) mutateResolveInjectedVariables(draft, timestamp);
          }),
          false,
          `Inject recursive row for section ${section.id}`
        );
        return;
      }

      // Inject segments in reverse order.
      let childOffset = 0;
      for (const segment of [...segments].reverse()) {
        if (segment.type === 'parent') {
          if (segment.containers.length === 0) continue;
          const content = buildContentFromContainers(segment.containers);
          const substituted = substituteRecursiveVariables(content, row, modelName, { remapVariableKey });
          if (!substituted.containers?.length) continue;
          set(
            produce((draft) => {
              mutateInjectSection({
                draft,
                sectionContent: substituted,
                boxId: targetBoxId,
                sectionId: section.id,
                dateTimestamp: timestamp,
                skipContainerRemoval: true,
                sectionsIds: [...basePath, section.id],
                visitedPath: nextVisitedPath,
              });
              if (remapVariableKey) mutateResolveInjectedVariables(draft, timestamp);
            }),
            false,
            `Inject parent segment for section ${section.id}`
          );
        } else {
          const { childTemplate, childModelName } = segment.config;
          const childSectionWork = childTemplate.work ?? childTemplate.content?.work ?? sectionWork;
          const childRows = await getRecursiveSectionData(
            opportunityId,
            childModelName,
            { parentModel: modelName, parentId: row._id, documentType, agreementType, sectionWork: childSectionWork }
          );
          if (!childRows || childRows.length === 0) continue;

          const childBaseTimestamp = Date.now() + childOffset;
          const reversedChildRows = [...childRows].reverse();
          for (let ci = 0; ci < reversedChildRows.length; ci++) {
            const childRow = reversedChildRows[ci];
            const childTimestamp = childBaseTimestamp + ci;
            // noRepeat : gardé une fois par ligne parente (celle-ci), sur la 1ère ligne
            // enfant. Boucle ascendante ici (contrairement aux lignes parentes) — le
            // dernier ci traité est celui qui finit visuellement en haut, cf. prepend.
            const isFirstChildRow = ci === reversedChildRows.length - 1;
            const childTemplateContent = isFirstChildRow
              ? childTemplate.content
              : filterSectionContentNoRepeat(childTemplate.content);
            const childContent = substituteRecursiveVariables(
              childTemplateContent,
              childRow,
              childModelName,
              { remapVariableKey }
            );
            set(
              produce((draft) => {
                mutateInjectSection({
                  draft,
                  sectionContent: childContent,
                  boxId: targetBoxId,
                  sectionId: childTemplate.id,
                  dateTimestamp: childTimestamp,
                  skipContainerRemoval: true,
                  sectionsIds: [...basePath, section.id, childTemplate.id],
                  // Scope a SECTION_LINK grandchild to THIS child row: it expands later via
                  // sectionsToActivate -> handleSectionAction, which forwards parentModel/parentId
                  // (and the overrides) into its own injectRecursiveSection call.
                  parentModel: childModelName,
                  parentId: childRow._id,
                  opportunityIdOverride,
                  documentTypeOverride,
                  visitedPath: nextVisitedPath,
                });
                if (remapVariableKey) mutateResolveInjectedVariables(draft, childTimestamp);
              }),
              false,
              `Inject child recursive row for section ${childTemplate.id}`
            );
            childOffset += 1;

            // TABLE_VARIABLE boxes owned by this child (e.g. formation's cost table) never
            // go through fillEmptyAdminTablesBoxes/zoneSplitTableData above -- those only cover
            // section.content (the PARENT's own boxes). Fill them here explicitly, split by
            // childRow._id when the model supports it (same convention as applyZoneTableData).
            if (documentType !== 'template') {
              const childTableBoxes = get().boxes.filter(
                (b) =>
                  b.dateTimestamp === childTimestamp &&
                  isATableBox(b.type) &&
                  b.tableName &&
                  (b.content?.data?.length ?? 0) === 0
              );
              if (childTableBoxes.length > 0) {
                const scopedRowId = SPLIT_MODELS.includes(childModelName)
                  ? childRow._id
                  : null;
                for (const tableBox of childTableBoxes) {
                  await fetchTableData({
                    fetchStoreFunc: get().fetchTable,
                    updateBoxFunc: get().updateBox,
                    box: tableBox,
                    isNewBox: false,
                    split: scopedRowId != null,
                    recursiveRowId: scopedRowId,
                  });
                }
              }
            }

            // Depth >= 3: this child template may itself embed recursive sections
            // (e.g. tenue -> clothing). Expand them now, scoped to THIS child row, before the
            // unscoped hydration / sectionsToActivate path can pick them up as a root fetch.
            const nestedRecursiveBoxes = get().boxes.filter(
              (b) =>
                b.dateTimestamp === childTimestamp &&
                b.type === ItemTypes.SECTION_VARIABLE &&
                b.sectionId
            );
            for (const nestedBox of nestedRecursiveBoxes) {
              const grandSection = await get().getTemplate(nestedBox.sectionId);
              if (!grandSection || grandSection.error || !grandSection.content) continue;
              const grandMeta = getSectionRecursiveMeta(grandSection);
              if (!grandMeta.isRecursive || !grandMeta.recursiveModelName) continue;
              await get().injectRecursiveSection({
                section: {
                  ...grandSection,
                  isRecursive: grandMeta.isRecursive,
                  recursiveModelName: grandMeta.recursiveModelName,
                },
                boxId: nestedBox.id,
                parentModel: childModelName,
                parentId: childRow._id,
                opportunityIdOverride,
                documentTypeOverride,
                visitedPath: nextVisitedPath,
              });
            }
          }
          const childUsedFieldKeys = new Set();
          const childRecursivePrefix = `${childModelName}.`;
          for (const box of childTemplate.content.boxes) {
            const raw = box.content?.raw;
            if (!raw) continue;
            try {
              const parsed = JSON.parse(raw);
              Object.values(parsed.entityMap || {}).forEach((entity) => {
                if (entity.type !== 'VARIABLE') return;
                const k = entity.data?.key || '';
                if (k.startsWith(childRecursivePrefix)) {
                  const rest = k.slice(childRecursivePrefix.length);
                  if (!rest.includes('.')) childUsedFieldKeys.add(rest);
                }
              });
            } catch {}
          }
          await get().syncIndexedRecursiveVarsToStore(childModelName, childRows, childUsedFieldKeys, true, agreementType);
        }
      }
    };

    const sortedRows = [...rows].reverse();
    const baseTimestamp = Date.now();
    for (let i = 0; i < sortedRows.length; i++) {
      await injectWithChildren(sortedRows[i], boxId, baseTimestamp + i, rows.length - 1 - i);
    }

    // Remove the placeholder container now that all rows are injected.
    set(
      produce((draft) => mutateRemoveRecursivePlaceholder(draft, boxId)),
      false,
      `Remove recursive placeholder ${boxId}`
    );
    const usedFieldKeys = new Set();
    const recursivePrefix = `${modelName}.`;
    for (const box of section.content.boxes) {
      const raw = box.content?.raw;
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        Object.values(parsed.entityMap || {}).forEach((entity) => {
          if (entity.type !== 'VARIABLE') return;
          const k = entity.data?.key || '';
          if (k.startsWith(recursivePrefix)) {
            const rest = k.slice(recursivePrefix.length);
            if (!rest.includes('.')) usedFieldKeys.add(rest);
          }
        });
      } catch {}
    }
    // Union, pas écrasement : deux sections du MÊME modèle dans un document
    // référencent souvent des champs différents. En remplaçant, la 2e section
    // masquait (notUsed) au panneau les champs propres à la 1re. On cumule donc
    // les champs référencés par toutes les sections du modèle.
    set(
      produce((draft) => {
        const prev = draft.recursiveUsedFieldKeys[modelName] ?? [];
        draft.recursiveUsedFieldKeys[modelName] = [...new Set([...prev, ...usedFieldKeys])];
      }),
      false,
      `setRecursiveUsedFieldKeys ${modelName}`
    );
    // En mode append (modèle récursif d'offre : une section par opportunité), on
    // garde la portée par section. Sinon (sections racine d'une même proposition
    // partageant les mêmes lignes), on passe l'union pour que la re-création des
    // variables indexées ne remasque pas les champs des sections précédentes.
    const isAppendSync = opportunityIdOverride != null;
    const fieldKeysForSync = isAppendSync
      ? usedFieldKeys
      : new Set(get().recursiveUsedFieldKeys[modelName]);
    await get().syncIndexedRecursiveVarsToStore(modelName, rows, fieldKeysForSync, isAppendSync, agreementType);
    set(
      produce((draft) => {
        const already = draft.loadedRecursiveSections.some(
          (m) => m.modelName === modelName && m.opportunityId === opportunityId
        );
        if (!already) draft.loadedRecursiveSections.push({ modelName, opportunityId, agreementType, sectionWork, documentType });
      }),
      false,
      `registerLoadedRecursiveSection ${modelName}`
    );
  },

  handleSectionAction: async ({
    boxId,
    optionId,
    sectionId,
    action,
    areItemsChangedByTriggers,
    linked_section = false,
    fromSectionId = sectionId,
    dateTimestamp,
    fromBoxId,
    isSecondInjection = false,
    from_linked_section,
    sectionsIds,
    wasDocumentLoading,
    opportunityIdOverride = null,
    documentTypeOverride = null,
    parentModel = null,
    parentId = null,
    visitedPath = new Set(),
    validSectionIds,
  }) => {
    if (action === 'remove') {
      set(
        produce((draft) => {
          const box = draft.boxes.find((b) => b.id === boxId);
          if (box?.type === ItemTypes.SECTION_VARIABLE && optionId)
            box.answer = optionId;
          mutateDeleteSection({ draft, boxId, optionId });
        })
      );
      return;
    }
    const section = await get().getTemplate(sectionId);
    if (section?.error) return;
    const currentDocument = get().currentDocument;

    if (action === 'inject' || action === 'reinitialize') {
      const sectionIsRecursive = section.isRecursive ?? section.content?.isRecursive;
      const sectionRecursiveModelName = section.recursiveModelName ?? section.content?.recursiveModelName;
      if (sectionIsRecursive && sectionRecursiveModelName && currentDocument.type !== 'template') {
        if (
          wasDocumentLoading &&
          shouldSkipRecursiveReloadExpansion(get().boxes, {
            anchorBoxId: boxId,
            sectionId,
            modelName: sectionRecursiveModelName,
          })
        ) {
          return;
        }
        const recursiveSection = {
          ...section,
          isRecursive: sectionIsRecursive,
          recursiveModelName: sectionRecursiveModelName,
        };
        const scope = {
          ...(opportunityIdOverride != null ? { opportunityIdOverride, documentTypeOverride } : {}),
          ...(parentModel != null ? { parentModel, parentId } : {}),
        };
        // Réinitialiser supprime le contenu en place avant de réinjecter : les
        // lignes sont donc récupérées d'abord. Si l'appel échoue, on garde le
        // contenu tel quel plutôt que de laisser une section vide en base. Un
        // modèle réellement sans ligne ([]) vide toujours la section.
        let prefetchedRows;
        if (action === 'reinitialize') {
          prefetchedRows = await fetchRecursiveSectionRows(currentDocument, {
            section: recursiveSection,
            ...scope,
          });
          if (prefetchedRows === null) {
            message.error(
              'Impossible de récupérer les données de la section : son contenu a été conservé.'
            );
            return;
          }
        }
        // La réponse est portée par la box. Le chemin non récursif la pose dans le
        // produce commun, plus bas, que ce retour anticipé n'atteint jamais : sans
        // ça la question se réaffiche vierge alors que son contenu est injecté.
        set(
          produce((draft) => {
            const anchorBox = draft.boxes.find((b) => b.id === boxId);
            if (optionId && anchorBox?.type === ItemTypes.SECTION_VARIABLE) {
              anchorBox.answer = optionId;
            }
            if (action === 'reinitialize') mutateDeleteSection({ draft, boxId, optionId });
          })
        );
        await get().injectRecursiveSection({
          section: recursiveSection,
          boxId,
          split: false,
          ...scope,
          visitedPath,
          prefetchedRows,
        });
        return;
      }
    }

    const sectionContent =
      currentDocument.type === 'template'
        ? section.content
        : {
            ...section.content,
            ...(await fillEmptyAdminTablesBoxes({
              boxes: section.content.boxes,
              containers: section.content.containers,
              columns: section.content.columns,
              proposable_id: opportunityIdOverride ?? currentDocument.id,
              type: documentTypeOverride ?? currentDocument.type,
              landscape: get().landscape,
              marginRight:
                section.content.configuration.margins.right * gridUnit,
              marginLeft: section.content.configuration.margins.left * gridUnit,
              header: section.content.masks.find(
                ({ id }) => id === MASKS_TYPE.HEADER.id
              ),
              footer: section.content.masks.find(
                ({ id }) => id === MASKS_TYPE.FOOTER.id
              ),
              gridConfiguration: section.content.gridConfiguration,
              configuration: section.content.configuration,
              triggers: section.content.triggers,
              variableOrderArray: section.content.variableOrderArray,
            })),
          };
    // Section non récursive posée sous une copie de modèle récursif (ou sous
    // une ligne récursive) : ses propres sections liées doivent garder la même
    // opportunité / ligne parente, sinon une récursive imbriquée plus bas
    // repart sur toute l'offre.
    const nestedScope = {
      ...(opportunityIdOverride != null ? { opportunityIdOverride, documentTypeOverride } : {}),
      ...(parentModel != null ? { parentModel, parentId } : {}),
    };
    set(
      produce((draft) => {
        const box = draft.boxes.find((b) => b.id === boxId);
        if (action === 'inject') {
          if (linked_section) {
            const hasInfiniteLoop = mutateHandleLinkedSectionRecursive({
              sectionsIds,
              box,
              draft,
              fromBoxId,
              boxId,
              sectionContent,
              isSecondInjection,
            });
            if (hasInfiniteLoop) return;
          }
          if (box.type === ItemTypes.SECTION_VARIABLE && optionId)
            box.answer = optionId;
          mutateInjectSection({
            draft,
            sectionContent,
            boxId,
            sectionId: section.id,
            areItemsChangedByTriggers,
            linked_section,
            fromSectionId,
            dateTimestamp,
            fromBoxId,
            from_linked_section,
            sectionsIds,
            wasDocumentLoading,
            ...nestedScope,
            visitedPath,
            validSectionIds,
          });
        } else if (action === 'reinitialize') {
          mutateDeleteSection({ draft, boxId, optionId });
          mutateInjectSection({
            draft,
            sectionContent,
            boxId,
            sectionId: sectionId,
            areItemsChangedByTriggers,
            linked_section,
            fromSectionId,
            dateTimestamp,
            fromBoxId,
            from_linked_section,
            sectionsIds,
            wasDocumentLoading,
            ...nestedScope,
            visitedPath,
            validSectionIds,
          });
        }
      }),
      false,
      `handleSectionAction from section ${boxId}`
    );
  },

  injectRecursiveModel: async ({ box, templatesByWork }) => {
    const currentDocument = get().currentDocument;
    const offerId = currentDocument.id;

    const oppRows = await getRecursiveSectionData(offerId, 'opportunity', { documentType: 'offer' });
    if (!oppRows || oppRows.length === 0) return;

    // Group by work, preserving first-appearance order
    const seenWorks = [];
    for (const row of oppRows) {
      if (!seenWorks.includes(row.work)) seenWorks.push(row.work);
    }
    const ordered = [];
    for (const work of seenWorks) {
      ordered.push(...oppRows.filter((r) => r.work === work));
    }

    // Warn once per work that has no template
    const skippedWorks = seenWorks.filter((w) => !templatesByWork[w]);
    for (const w of skippedWorks) {
      message.warning(`Métier sans modèle : les opportunités de type "${w}" seront ignorées.`);
    }

    const injectables = ordered.filter((r) => templatesByWork[r.work]);

    // noRepeat scope = par template, pas par document entier. Un métier groupé après un
    // autre (ex: guarding après electronic security) ne doit pas perdre son propre contenu
    // noRepeat juste parce qu'il n'occupe pas l'index 0 global.
    const firstIndexPerTemplate = new Map();
    injectables.forEach((r, idx) => {
      const tplId = templatesByWork[r.work];
      if (!firstIndexPerTemplate.has(tplId)) firstIndexPerTemplate.set(tplId, idx);
    });

    if (injectables.length === 0) {
      // Remove placeholder
      set(
        produce((draft) => {
          const placeholderBox = draft.boxes.find((b) => b.id === box.id);
          if (!placeholderBox) return;
          const containerIdx = draft.containers.findIndex((c) =>
            c.columnsIds.includes(placeholderBox.columnId)
          );
          if (containerIdx !== -1) mutateRemoveContainer(draft, draft.containers[containerIdx].id);
        }),
        false,
        `Remove recursive_model placeholder ${box.id}`
      );
      return;
    }

    // Inject in reverse order so prepend yields correct final order
    for (let i = injectables.length - 1; i >= 0; i--) {
      const opp = injectables[i];
      const tpl = await get().getTemplate(templatesByWork[opp.work]);
      if (!tpl || tpl.error || !tpl.content) {
        message.error(`Impossible de charger le modèle pour le métier "${opp.work}". L'opportunity "${opp.name || opp._id}" sera ignorée.`);
        continue;
      }

      const timestamp = Date.now() + i;

      // noRepeat : gardé une seule fois par template, à sa première apparition dans
      // injectables (peu importe sa position dans le document global).
      const isFirstOccurrenceOfTemplate = firstIndexPerTemplate.get(tpl.id) === i;
      const tplContent = isFirstOccurrenceOfTemplate ? tpl.content : filterSectionContentNoRepeat(tpl.content);

      // Le gabarit est écrit pour UNE opportunité : ses variables document
      // (opportunity.NAME, entity.ADDRESS, user.PRESENTATION...) sont des clés
      // à 2 segments, qui dans une offre ne désignent rien — une offre porte
      // plusieurs opportunités. L'offre expose justement chacune d'elles en
      // clés à 3 segments CHRONO.categorie.information (cf. formatVariables,
      // utils/dataSync.js), déjà construites au chargement. On rattache donc
      // chaque copie à SON opportunité en préfixant ses clés par le chrono de
      // la ligne. Le test d'existence sert de filtre : les variables hors
      // périmètre opportunité (custom.*, offer.*) n'ont pas d'équivalent
      // préfixé et restent intactes.
      const remapVariableKey = buildVariableRemap(opp.chrono, get().variables);

      const substitutedContent = substituteRecursiveVariables(
        tplContent,
        opp,
        'opportunity',
        { remapVariableKey }
      );

      set(
        produce((draft) => {
          mutateInjectSection({
            draft,
            sectionContent: substitutedContent,
            boxId: box.id,
            sectionId: tpl.id,
            dateTimestamp: timestamp,
            skipContainerRemoval: true,
            opportunityIdOverride: opp._id,
            documentTypeOverride: 'opportunity',
          });
          if (remapVariableKey) mutateResolveInjectedVariables(draft, timestamp);
        }),
        false,
        `injectRecursiveModel row ${i} for box ${box.id}`
      );

      // Inject nested recursive sections scoped to this opportunity
      const injectedBoxes = get().boxes.filter(
        (b) => b.dateTimestamp === timestamp && b.type === ItemTypes.SECTION_VARIABLE && b.sectionId
      );
      for (const injectedBox of injectedBoxes) {
        const childSection = await get().getTemplate(injectedBox.sectionId);
        if (!childSection || childSection.error || !childSection.content) continue;
        const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(childSection);
        if (!isRecursive || !recursiveModelName) continue;
        await get().injectRecursiveSection({
          section: { ...childSection, isRecursive, recursiveModelName },
          boxId: injectedBox.id,
          opportunityIdOverride: opp._id,
          documentTypeOverride: 'opportunity',
        });
      }
    }

    // Remove placeholder
    set(
      produce((draft) => {
        const placeholderBox = draft.boxes.find((b) => b.id === box.id);
        if (!placeholderBox) return;
        const containerIdx = draft.containers.findIndex((c) =>
          c.columnsIds.includes(placeholderBox.columnId)
        );
        if (containerIdx !== -1) mutateRemoveContainer(draft, draft.containers[containerIdx].id);
      }),
      false,
      `Remove recursive_model placeholder ${box.id}`
    );
  },

  /**
   * After opening a proposal copied from a template: re-expand embedded recursive
   * sections that were saved as a single placeholder row (template-mode inject).
   */
  hydrateEmbeddedRecursiveSections: async () => {
    const { boxes, currentDocument } = get();
    if (currentDocument.type === 'template' || !currentDocument.id) return;

    const sectionVariableBoxes = boxes.filter(
      (b) => b.type === ItemTypes.SECTION_VARIABLE && b.sectionId
    );

    for (const box of sectionVariableBoxes) {
      const section = await get().getTemplate(box.sectionId);
      if (section?.error) continue;
      const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(section);
      if (!isRecursive || !recursiveModelName) continue;

      const hasChildren = get().boxes.some((b) => b.fromBoxId === box.id);
      if (!hasChildren) continue;
      if (
        !hasUnresolvedRecursiveVariables(get().boxes, recursiveModelName, {
          fromBoxId: box.id,
        })
      ) {
        continue;
      }

      await get().handleSectionAction({
        boxId: box.id,
        sectionId: box.sectionId,
        action: 'reinitialize',
        linked_section: false,
      });
    }

    const handledAnchors = new Set(
      sectionVariableBoxes.map((b) => b.id).filter(Boolean)
    );

    const bakedInstances = getBakedRecursiveInstances(get().boxes);
    for (const instance of bakedInstances) {
      if (instance.fromBoxId && handledAnchors.has(instance.fromBoxId)) continue;

      const section = await get().getTemplate(instance.fromSectionId);
      if (section?.error) continue;
      const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(section);
      if (!isRecursive || !recursiveModelName) continue;

      if (
        !hasUnresolvedRecursiveVariables(get().boxes, recursiveModelName, {
          fromSectionId: instance.fromSectionId,
          dateTimestamp: instance.dateTimestamp,
          fromBoxId: instance.fromBoxId,
        })
      ) {
        continue;
      }

      const anchor = instance.fromBoxId
        ? get().boxes.find((b) => b.id === instance.fromBoxId)
        : null;

      if (anchor?.type === ItemTypes.SECTION_VARIABLE && anchor.sectionId) {
        await get().handleSectionAction({
          boxId: anchor.id,
          sectionId: anchor.sectionId,
          action: 'reinitialize',
          linked_section: false,
        });
      }
    }
  },
});
